import {Component, computed, inject, model, signal, viewChild, ViewEncapsulation} from '@angular/core';
import {DxPopupModule} from 'devextreme-angular';
import {SharedModule} from '../../../../../shared/shared.module';
import {ButtonThemeEnum} from '../../../../../shared/ui/button/button-theme-enum';
import {PlcProduitComplementaireDTO} from '../../../../../core/dtos/plc-produit-complementaire-dto';
import {JourToggle} from '../../../../../core/models/gestion-produits/declinaison/complementaire/plc/jour-toggle';
import {
  SelectionProduitComplementaire
} from '../../../../../core/models/gestion-produits/declinaison/complementaire/selection-produit-complementaire';
import {PlcProduitComplementaireService} from '../../../../../core/services/entities/plc-produit-complementaire.service';
import {
  ProduitComplementaireParametrageService
} from '../../../../../core/services/entities/produit-complementaire-parametrage.service';
import {
  DialogSelectionProduitComplementaireComponent
} from '../dialog-selection-produit-complementaire/dialog-selection-produit-complementaire.component';
import {
  DialogPeriodeConditionnementComponent
} from '../../../../../shared/produits-complementaires/dialog-periode-conditionnement/dialog-periode-conditionnement.component';
import {ProduitComplementaireGridComponent} from '../../../../../shared/produits-complementaires/produit-complementaire-grid/produit-complementaire-grid.component';
import {
  PeriodeConditionnementDTO
} from '../../../../../core/dtos/produit/declinaison/complementaire/periode-conditionnement-dto';
import {ToastService} from '../../../../../core/services/technique/toast.service';
import {MSG_KEY, MSG_SEVERITY} from '../../../../../core/constants';

@Component({
  selector: 'yo-dialog-ajout-produit-complementaire',
  templateUrl: './dialog-ajout-produit-complementaire.component.html',
  styleUrls: ['./dialog-ajout-produit-complementaire.component.scss'],
  standalone: true,
  imports: [DxPopupModule, SharedModule, ProduitComplementaireGridComponent,
            DialogSelectionProduitComplementaireComponent, DialogPeriodeConditionnementComponent],
  encapsulation: ViewEncapsulation.None
})
export class DialogAjoutProduitComplementaireComponent {

  readonly visible = model<boolean>(false);

  readonly ButtonThemeEnum = ButtonThemeEnum;

  private readonly plcProduitComplementaireSvc = inject(PlcProduitComplementaireService);
  private readonly toastSvc = inject(ToastService);
  private readonly paramSvc = inject(ProduitComplementaireParametrageService);

  private readonly grid = viewChild(ProduitComplementaireGridComponent);

  readonly propositionList = signal<PlcProduitComplementaireDTO[]>([]);
  readonly selectedIdList = computed(() => this.propositionList().map(ligne => ligne.produitDeclinaisonId));

  readonly selectionVisible = signal<boolean>(false);

  readonly dialogPeriodeVisible = signal<boolean>(false);

  readonly produitPeriode = signal<PlcProduitComplementaireDTO>(null);

  onShowing = (): void => this.propositionList.set([]);

  openSelection = (): void => this.selectionVisible.set(true);

  fermer = (): void => this.visible.set(false);

  openDialogPeriode = (produitComplementaire: PlcProduitComplementaireDTO): void => {
    this.produitPeriode.set(produitComplementaire);
    this.dialogPeriodeVisible.set(true);
  };

  onPeriodeListChange = (periodeList: PeriodeConditionnementDTO[]): void => {
    const produitDeclinaisonId = this.produitPeriode()?.produitDeclinaisonId;
    this.propositionList.update(current => current.map(ligne => ligne.produitDeclinaisonId !== produitDeclinaisonId
      ? ligne
      : {...ligne, periodeConditionnementList: periodeList}));
  };

  onSelectionValidee = (selection: SelectionProduitComplementaire): void => {
    const declinaisonByIdMap = new Map(selection.declinaisonList.map(declinaison => [declinaison.id, declinaison]));

    this.propositionList.update(current => {
      const ligneByIdMap = new Map(current.map(ligne => [ligne.produitDeclinaisonId, ligne]));
      return selection.idList
                      .filter(id => ligneByIdMap.has(id) || declinaisonByIdMap.has(id))
                      .map(id => ligneByIdMap.get(id) ?? this.paramSvc.buildLigne(declinaisonByIdMap.get(id)));
    });
  };

  retirerDeLaProposition = (produitComplementaire: PlcProduitComplementaireDTO): void =>
    this.propositionList.update(current =>
      current.filter(ligne => ligne.produitDeclinaisonId !== produitComplementaire.produitDeclinaisonId));

  toggleJour = (jour: JourToggle): void =>
    this.propositionList.update(current =>
      this.paramSvc.toggleJourConsommation(current, jour.produitDeclinaisonId, jour.jourId));

  ajouter = (): void => {
    this.grid()?.saveEditData().then(() => {
      const propositionList = this.propositionList();

      const errorMessage = this.paramSvc.buildParametrageIncompletMessage(propositionList);
      if (errorMessage) {
        this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.ERROR, errorMessage);
        return;
      }

      this.plcProduitComplementaireSvc.addProduits(propositionList)
          .subscribe(() => {
            this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS,
              `${propositionList.length} produit(s) complémentaire(s) ajouté(s) avec succès`);
            this.visible.set(false);
          });
    });
  };
}
